import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Hash, Building2, AlertTriangle, Clock, Package } from "lucide-react";
import { categoryIcons } from "./serialIcons";

export function SerialNumberStatistics({ statistics }) {
  if (!statistics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Hash className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statistics.total}</p>
                <p className="text-sm text-muted-foreground">Gesamt</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{statistics.expired}</p>
                <p className="text-sm text-muted-foreground">Abgelaufen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{statistics.expiring_soon}</p>
                <p className="text-sm text-muted-foreground">Läuft bald ab</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statistics.by_customer?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Kunden</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Nach Kategorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statistics.by_category?.map((item) => {
                const CategoryIcon = categoryIcons[item.category] || Package;
                const percentage = Math.round((item.count / statistics.total) * 100);
                return (
                  <div key={`category-${item.category}`} className="flex items-center gap-3">
                    <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1 text-sm">{item.category}</span>
                    <span className="text-sm font-medium">{item.count}</span>
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!statistics.by_category || statistics.by_category.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Keine Daten</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Customer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top 10 Kunden</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statistics.by_customer?.map((item) => {
                const percentage = Math.round((item.count / statistics.total) * 100);
                return (
                  <div key={`customer-${item.customer}`} className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="flex-1 text-sm truncate">{item.customer}</span>
                    <span className="text-sm font-medium">{item.count}</span>
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!statistics.by_customer || statistics.by_customer.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">Keine Daten</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring by Month Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ablaufende Geräte pro Monat</CardTitle>
          <CardDescription>Übersicht der nächsten 12 Monate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 h-40">
            {statistics.expiring_by_month?.map((item) => {
              const maxCount = Math.max(...statistics.expiring_by_month.map(m => m.count), 1);
              const height = (item.count / maxCount) * 100;
              return (
                <div key={`month-${item.month_name}`} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-medium">{item.count}</span>
                  <div
                    className={`w-full rounded-t transition-all ${
                      item.count > 0 ? 'bg-amber-500' : 'bg-muted'
                    }`}
                    style={{ height: `${Math.max(height, 4)}%` }}
                  />
                  <span className="text-xs text-muted-foreground rotate-45 origin-left translate-x-2">
                    {item.month_name}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
